/**
 * @file XeroVersionDialog.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-20
 * @modified 2026-10-20
 * @description "Accept Xero version" for a Xero-made purchase order (Cam,
 *              20 Oct 2026 — "If Xero differs I need to be offered to accept
 *              the Xero version from the admin issues log please"). Admins
 *              only (the server refuses anyone else).
 *
 * Opens on GET /api/purchase-orders/db/:id/xero-version — Xero read NOW — and
 * shows line by line what accepting changes (ours → Xero's), the lines kept
 * (bought for an order line, or supplied) and removed, or why it cannot be
 * accepted. "Accept Xero version" posts with the Xero version shown, so a
 * copy that changed again in between is refused, not taken blind. "Keep
 * ours" (from Issues Logged) records the change as answered and changes
 * nothing. Both are confirmed in this window — nothing is written on open.
 */

import React, { useEffect, useState } from 'react';
import Modal from './Modal';
import LoadingSpinner from './LoadingSpinner';
import { API_URL } from '../config';
import { useSlideOutButton } from './SlideOutButton';
import { formatQuantity } from '../utils/numberFormatters';
import { formatPrice, formatCurrency } from '../utils/currencyFormatter';
import './XeroVersionDialog.css';

const FIELD_LABEL = {
  quantity: 'Qty', unitAmount: 'Cost', description: 'Words', itemCode: 'Item', accountCode: 'Account', taxType: 'Tax'
};
const HEADER_LABEL = { status: 'Status', date: 'Date', delivery_date: 'Delivery date', reference: 'Reference', total: 'Total' };
const CHANGE_LABEL = { changed: 'Changes', same: 'Same', added: 'Added from Xero', removed: 'Removed (not in Xero)', kept: 'Kept (only ours)' };

const dmy = (v) => (v && /^\d{4}-\d{2}-\d{2}/.test(String(v)) ? String(v).slice(0, 10).split('-').reverse().join('/') : (v || '—'));
const show = (k, v) => {
  if (v === null || v === undefined || v === '') return '—';
  if (k === 'quantity') return formatQuantity(v);
  if (k === 'unitAmount') return formatPrice(v);
  if (k === 'total') return formatCurrency(v);
  if (k === 'date' || k === 'delivery_date') return dmy(v);
  return String(v);
};

function LineFace({ face }) {
  if (!face) return <span className="xv-muted">—</span>;
  return (
    <span>
      {face.itemCode ? <strong>{face.itemCode} </strong> : null}
      {face.description || ''}
      <span className="xv-muted"> · {formatQuantity(face.quantity)} @ {formatPrice(face.unitAmount)}</span>
    </span>
  );
}

function Body({ preview, loading, error }) {
  if (loading) return <div className="xv-loading"><LoadingSpinner /> Reading Xero's copy…</div>;
  if (error) return <div className="xv-error"><span className="material-symbols-rounded">error</span> {error}</div>;
  if (!preview) return null;
  if (preview.refusal) {
    return <div className="xv-error"><span className="material-symbols-rounded">block</span> {preview.refusal}</div>;
  }
  if (!preview.changed) {
    return <div className="xv-same"><span className="material-symbols-rounded">check_circle</span> Our copy of {preview.purchase_order?.number} already matches Xero's — nothing to accept.</div>;
  }
  const lines = (preview.lines || []).filter(l => l.change !== 'same');
  return (
    <>
      <p className="xv-intro">
        Accepting puts Xero's copy of <strong>{preview.purchase_order?.number}</strong> over ours (ours → Xero's).
        Order links, haulier / driver / vehicle, supplied quantities and contract numbers stay ours. Nothing is sent to Xero.
      </p>
      {(preview.header || []).length > 0 && (
        <table className="xv-table">
          <thead><tr><th>PO</th><th>Ours</th><th>Xero's</th></tr></thead>
          <tbody>
            {preview.header.map(h => (
              <tr key={h.field}>
                <td>{HEADER_LABEL[h.field] || h.field}</td>
                <td>{show(h.field, h.ours)}</td>
                <td>{show(h.field, h.xero)}{h.note ? <div className="xv-muted">{h.note}</div> : null}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <table className="xv-table">
        <thead><tr><th>Line</th><th>Ours</th><th>Xero's</th><th>What changes</th></tr></thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={`${l.label}-${i}`} className={`xv-${l.change}`}>
              <td>{l.label}</td>
              <td><LineFace face={l.ours} /></td>
              <td><LineFace face={l.xero} /></td>
              <td>
                <span className={`xv-tag xv-tag-${l.change}`}>{CHANGE_LABEL[l.change] || l.change}</span>
                {Object.entries(l.fields || {}).map(([k, [a, b]]) => (
                  <div key={k} className="xv-field">{FIELD_LABEL[k] || k}: {show(k, a)} → {show(k, b)}</div>
                ))}
                {l.why && <div className="xv-muted">{l.why}</div>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {(preview.order_linked || []).length > 0 && (
        <p className="xv-note">
          <span className="material-symbols-rounded">info</span>
          {preview.order_linked.map(o => o.label).join(', ')}: bought for an order line — the order line's quantity is not changed. Change it on the order if it should follow.
        </p>
      )}
    </>
  );
}

function Footer({ onCancel, onKeep, onAccept, canAccept, busy, keepOffered }) {
  useSlideOutButton([busy, canAccept, keepOffered]);
  return (
    <div className="buttons slideout">
      <div><button className="btn-slideout" onClick={onCancel} disabled={!!busy}>
        <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
        <span className="btn-label">Cancel</span>
      </button></div>
      {keepOffered && (
        <div><button className="btn-slideout" onClick={onKeep} disabled={!!busy} title="Keep our copy as it is and mark this change as answered" data-write-action>
          <div className="icon-wrapper"><span className="material-symbols-rounded">do_not_disturb_on</span></div>
          <span className="btn-label">{busy === 'keep' ? 'Saving…' : 'Keep ours'}</span>
        </button></div>
      )}
      <div><button className="btn-slideout" onClick={onAccept} disabled={!canAccept || !!busy}
        title={canAccept ? 'Overwrite our copy with Xero\'s, as shown' : 'Nothing to accept'} data-write-action>
        <div className="icon-wrapper"><span className="material-symbols-rounded">cloud_download</span></div>
        <span className="btn-label">{busy === 'accept' ? 'Accepting…' : 'Accept Xero version'}</span>
      </button></div>
    </div>
  );
}

/**
 * @param {{ poId: number, poNumber?: string, xeroUpdated?: string|null,
 *   keepOffered?: boolean, onClose: () => void, onDone?: (r: object) => void }} props
 */
export default function XeroVersionDialog({ poId, poNumber, xeroUpdated = null, keepOffered = false, onClose, onDone }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/purchase-orders/db/${poId}/xero-version`, { credentials: 'include' });
        const body = await res.json().catch(() => ({}));
        if (!live) return;
        if (!res.ok) setError(body.error || `HTTP ${res.status}`);
        else setPreview(body);
      } catch (e) {
        if (live) setError(e.message || String(e));
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [poId]);

  const post = async (which, path, body) => {
    setBusy(which);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/purchase-orders/db/${poId}/xero-version/${path}`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
      });
      const out = await res.json().catch(() => ({}));
      if (!res.ok) { setError(out.error || `HTTP ${res.status}`); return; }
      if (onDone) onDone({ action: which, ...out });
      onClose();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(null);
    }
  };

  const number = preview?.purchase_order?.number || poNumber || 'this PO';
  const canAccept = !!preview && !preview.refusal && preview.changed;
  return (
    <Modal isOpen onClose={onClose} title={`Accept Xero's version of ${number}?`} size="lg" className="xero-version"
      footer={<Footer onCancel={onClose} keepOffered={keepOffered} busy={busy} canAccept={canAccept}
        onKeep={() => post('keep', 'keep', { xeroUpdated: xeroUpdated || preview?.xero_updated || null })}
        onAccept={() => post('accept', 'accept', { expectXeroUpdated: preview?.xero_updated || null })} />}>
      <Body preview={preview} loading={loading} error={preview ? null : error} />
      {preview && error && <div className="xv-error"><span className="material-symbols-rounded">error</span> {error}</div>}
    </Modal>
  );
}
